// src/routes/assets/index.tsx
import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useCallback } from 'react';
import { useSnapshot } from 'valtio';
import { assetStore, assetActions } from '../../stores/assetStore';
import { NavigationSidebar } from '../../components/layout/NavigationSidebar';
import { ModuleHeader } from '../../components/layout/ModuleHeader';
import { Icon } from '../../components/ui/Icon';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { useExpiry } from '../../hooks/useExpiry';
import { formatDate } from '../../utils/dateUtils';
import { exportToCSV, prepareAssetExportData } from '../../utils/exportUtils';
import { getAssetDisplayName } from '../../utils/assetDisplayName';
import { BarcodeScanner } from '../../components/assets/BarcodeScanner';
import { QRGenerator } from '../../components/assets/QRGenerator';
import { CSVImport } from '../../components/assets/CSVImport';
import { iso27001Categories } from '../../data/assetCategories';
import type { Asset, AssetFormData } from '../../types/asset';

export const Route = createFileRoute('/assets/')({
  component: AssetInventoryModule,
});

function AssetInventoryModule() {
  const snap = useSnapshot(assetStore);
  const { showToast, ToastContainer } = useToast();

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [showScannerModal, setShowScannerModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Item states
  const [editingItem, setEditingItem] = useState<Asset | null>(null);
  const [auditingItem, setAuditingItem] = useState<Asset | null>(null);
  const [qrItem, setQRItem] = useState<Asset | null>(null);
  const [historyItem, setHistoryItem] = useState<Asset | null>(null);
  const [scannerMode, setScannerMode] = useState<'search' | 'sku'>('search');

  const initialFormData: AssetFormData = {
    referenceNumber: '',
    make: '',
    model: '',
    assetGroup: '',
    sku: '',
    description: '',
    owner: '',
    assetOwner: '',
    location: '',
    status: 'Active',
    quantity: 0,
    acquisitionDate: '',
    cost: '',
    maintenanceSchedule: '',
    lastInspectionDate: '',
    nextInspectionDate: '',
    isISO27001: false,
    iso27001Category: '',
    auditDate: '',
    auditPeriodicity: '',
    calibrationDate: '',
    calibrationPeriodicity: '',
    certificationDate: '',
    certificationPeriodicity: '',
    testDate: '',
    testPeriodicity: '',
    datesApplicable: true,
    notes: '',
  };

  const [formData, setFormData] = useState<AssetFormData>(initialFormData);
  const [auditForm, setAuditForm] = useState({
    actualQuantity: '',
    discrepancy: '',
    notes: '',
    auditor: '',
  });

  // Local filter state for location and owner
  const [locationFilter, setLocationFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');

  // Filter logic
  const filteredItems = useMemo(() => {
    return snap.assets.filter((item) => {
      const term = snap.searchTerm.toLowerCase();
      const matchesSearch = !snap.searchTerm ||
        (item.make?.toLowerCase().includes(term)) ||
        (item.model?.toLowerCase().includes(term)) ||
        (item.assetGroup?.toLowerCase().includes(term)) ||
        item.sku.toLowerCase().includes(term) ||
        item.referenceNumber.toLowerCase().includes(term);

      const matchesISO =
        snap.iso27001Filter === 'all' ||
        (snap.iso27001Filter === 'yes' && item.isISO27001) ||
        (snap.iso27001Filter === 'no' && !item.isISO27001);

      const matchesLocation =
        locationFilter === 'all' || item.location === locationFilter;

      const matchesOwner =
        ownerFilter === 'all' || item.owner === ownerFilter || item.assetOwner === ownerFilter;

      return matchesSearch && matchesISO && matchesLocation && matchesOwner;
    });
  }, [snap.assets, snap.searchTerm, snap.iso27001Filter, locationFilter, ownerFilter]);

  const locations = useMemo(
    () => [...new Set(snap.assets.map((i) => i.location).filter(Boolean))],
    [snap.assets]
  );

  const owners = useMemo(
    () => [...new Set(snap.assets.map((i) => i.owner || i.assetOwner).filter(Boolean))],
    [snap.assets]
  );

  // Sorting
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = useCallback((column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn]);

  const sortedItems = useMemo(() => {
    if (!sortColumn) return filteredItems;
    return [...filteredItems].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';
      switch (sortColumn) {
        case 'reference': aVal = a.referenceNumber || ''; bVal = b.referenceNumber || ''; break;
        case 'make': aVal = a.make || ''; bVal = b.make || ''; break;
        case 'model': aVal = a.model || ''; bVal = b.model || ''; break;
        case 'assetGroup': aVal = a.assetGroup || ''; bVal = b.assetGroup || ''; break;
        case 'sku': aVal = a.sku || ''; bVal = b.sku || ''; break;
        case 'quantity': aVal = a.quantity || 0; bVal = b.quantity || 0; break;
        case 'location': aVal = a.location || ''; bVal = b.location || ''; break;
        case 'iso27001': aVal = a.isISO27001 ? 1 : 0; bVal = b.isISO27001 ? 1 : 0; break;
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [filteredItems, sortColumn, sortDirection]);

  // Scanner handlers
  const openScanner = (mode: 'search' | 'sku') => {
    setScannerMode(mode);
    setShowScannerModal(true);
  };

  const handleScanResult = (result: string) => {
    if (scannerMode === 'search') {
      const foundItem = snap.assets.find(
        (item) =>
          item.referenceNumber === result ||
          item.sku.toLowerCase() === result.toLowerCase()
      );

      if (foundItem) {
        assetActions.setSearchTerm(result);
        showToast(`Found: ${getAssetDisplayName(foundItem)}`, `success`);
      } else {
        showToast('No item found with this code', 'error');
      }
    } else {
      setFormData({ ...formData, sku: result });
      showToast('SKU code captured', 'success');
    }
  };

  const openQRGenerator = (item: Asset) => {
    setQRItem(item);
    setShowQRModal(true);
  };

  const openHistoryModal = (item: Asset) => {
    setHistoryItem(item);
    setShowHistoryModal(true);
  };

  const openAddModal = () => {
    setEditingItem(null);
    setFormData(initialFormData);
    setShowModal(true);
  };

  const openEditModal = (item: Asset) => {
    setEditingItem(item);
    setFormData({
      ...item,
      datesApplicable: item.datesApplicable !== undefined ? item.datesApplicable : true,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.sku) {
      showToast('Please fill in required fields', 'error');
      return;
    }

    if (formData.isISO27001 && !formData.iso27001Category) {
      showToast('Please select an Information Security Category', 'error');
      return;
    }

    // Require at least one date+periodicity when dates are applicable
    const effectiveDatesApplicable = formData.isISO27001 ? true : formData.datesApplicable;
    if (effectiveDatesApplicable) {
      const hasCompleteDatePair =
        (formData.auditDate && formData.auditPeriodicity) ||
        (formData.calibrationDate && formData.calibrationPeriodicity) ||
        (formData.certificationDate && formData.certificationPeriodicity) ||
        (formData.testDate && formData.testPeriodicity);
      if (!hasCompleteDatePair) {
        const label = formData.isISO27001 ? 'Information Security Assets' : 'Assets with compliance dates applicable';
        showToast(`${label} require at least one compliance date and periodicity`, 'error');
        return;
      }
    }

    try {
      if (editingItem) {
        await assetActions.updateAsset(editingItem.id, formData);
        showToast('Asset updated successfully', 'success');
      } else {
        const tempId = `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const newAsset: Asset = {
          ...formData,
          id: tempId,
          referenceNumber: '',
          owner: formData.assetOwner || formData.owner,  // Sync both fields
          assetOwner: formData.assetOwner || formData.owner,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        await assetActions.addAsset(newAsset);
        // After addAsset, the server response replaces the optimistic entry with real ID/ref
        const savedAsset = assetStore.assets.find(a => a.id !== tempId && a.referenceNumber) || newAsset;
        showToast(`Asset ${savedAsset.referenceNumber || 'created'} saved successfully`, `success`);

        if (confirm('Would you like to generate a barcode/QR code for this asset?')) {
          setQRItem(savedAsset);
          setShowQRModal(true);
        }
      }
      setShowModal(false);
    } catch (e: unknown) {
      const err = e as Error & { status?: number };
      if (err.status === 409) {
        showToast('This asset was modified by another user. The latest version has been loaded.', 'error');
      } else {
        showToast('Failed to save asset — please try again', 'error');
      }
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this asset?')) {
      assetActions.deleteAsset(id);
      showToast('Asset deleted successfully', 'success');
    }
  };

  const openAuditModal = (item: Asset) => {
    setAuditingItem(item);
    setAuditForm({
      actualQuantity: String(item.quantity),
      discrepancy: '',
      notes: '',
      auditor: '',
    });
    setShowAuditModal(true);
  };

  const handleAuditComplete = () => {
    if (!auditingItem) return;

    const auditRecord = {
      date: new Date().toISOString(),
      auditor: auditForm.auditor || 'Unknown',
      actualQuantity: parseInt(auditForm.actualQuantity),
      expectedQuantity: auditingItem.quantity,
      discrepancy: auditForm.discrepancy,
      discrepancyReason: auditForm.discrepancy,
      notes: auditForm.notes,
      status:
        parseInt(auditForm.actualQuantity) === auditingItem.quantity
          ? ('match' as const)
          : ('discrepancy' as const),
    };

    const existingHistory = auditingItem.auditHistory || [];
    assetActions.updateAsset(auditingItem.id, {
      quantity: parseInt(auditForm.actualQuantity),
      auditDate: new Date().toISOString().split('T')[0],
      auditHistory: [auditRecord, ...existingHistory],
    });

    setShowAuditModal(false);
    showToast('Audit completed successfully', 'success');
  };

  const handleExport = () => {
    const data = prepareAssetExportData(filteredItems as Asset[]);
    exportToCSV(data, 'asset-inventory');
    showToast('Export completed', 'success');
  };

  return (
    <div className="flex flex-1 overflow-hidden bg-gray-50">
      <NavigationSidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <ModuleHeader
          title="Asset Management"
          subtitle="Manage your complete asset inventory"
        />

        {/* Action Bar & Stats — fixed at top */}
        <div className="flex-shrink-0 p-6 pb-0">
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={openAddModal} icon="Plus">
                  Add Asset
                </Button>
                <Button variant="secondary" onClick={() => openScanner("search")} icon="Camera">
                  Scan
                </Button>
                <Button variant="secondary" onClick={() => setShowImportModal(true)} icon="Upload">
                  Import
                </Button>
                <Button variant="secondary" onClick={handleExport} icon="Download">
                  Export
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <Icon
                    name="Search"
                    size={20}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Search assets..."
                    value={snap.searchTerm}
                    onChange={(e) => assetActions.setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={snap.iso27001Filter}
                  onChange={(e) =>
                    assetActions.setISO27001Filter(e.target.value as 'all' | 'yes' | 'no')
                  }
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="all">All Assets</option>
                  <option value="yes">Info Sec Only</option>
                  <option value="no">Non Info Sec Only</option>
                </select>
                <select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="all">All Locations</option>
                  {locations.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
                <select
                  value={ownerFilter}
                  onChange={(e) => setOwnerFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="all">All Owners</option>
                  {owners.map((owner) => (
                    <option key={owner} value={owner}>
                      {owner}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-md p-4">
              <p className="text-gray-500 text-sm">Total Assets</p>
              <p className="text-2xl font-bold">{snap.assets.length}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <p className="text-gray-500 text-sm">Filtered</p>
              <p className="text-2xl font-bold">{filteredItems.length}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <p className="text-gray-500 text-sm">ISO 27001</p>
              <p className="text-2xl font-bold text-green-600">
                {snap.assets.filter((i) => i.isISO27001).length}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <p className="text-gray-500 text-sm">Incomplete</p>
              <p className="text-2xl font-bold text-amber-600">
                {snap.assets.filter((i) => getAssetMissingFields(i as Asset).length > 0).length}
              </p>
            </div>
          </div>
        </div>

        {/* Table — fills remaining space, scrolls independently */}
        <div className="flex-1 min-h-0 px-6 pb-6">
          <div className="bg-white rounded-lg shadow-md h-full overflow-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  {([
                    { key: 'reference', label: 'Reference' },
                    { key: 'make', label: 'Make' },
                    { key: 'model', label: 'Model' },
                    { key: 'assetGroup', label: 'Asset Group' },
                    { key: 'sku', label: 'SKU' },
                    { key: 'quantity', label: 'Qty' },
                    { key: 'location', label: 'Location' },
                    { key: 'iso27001', label: 'ISO 27001' },
                  ] as const).map(col => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className="px-4 py-3 text-left text-sm font-semibold text-gray-600 cursor-pointer select-none hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        {sortColumn === col.key ? (
                          <svg className="w-3.5 h-3.5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                            {sortDirection === 'asc'
                              ? <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L10 6.414l-3.293 3.293a1 1 0 01-1.414 0z" clipRule="evenodd" />
                              : <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L10 13.586l3.293-3.293a1 1 0 011.414 0z" clipRule="evenodd" />}
                          </svg>
                        ) : (
                          <svg className="w-3.5 h-3.5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedItems.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                      No assets found
                    </td>
                  </tr>
                ) : (
                  sortedItems.map((item) => (
                    <AssetRow
                      key={item.id}
                      item={item as Asset}
                      onEdit={openEditModal}
                      onDelete={handleDelete}
                      onAudit={openAuditModal}
                      onQR={openQRGenerator}
                      onHistory={openHistoryModal}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      {/* Scanner Modal */}
      <BarcodeScanner
        isOpen={showScannerModal}
        onClose={() => setShowScannerModal(false)}
        onScan={handleScanResult}
        mode={scannerMode}
      />

      {/* QR Generator Modal */}
      <QRGenerator
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        asset={qrItem}
        showToast={showToast}
      />

      {/* CSV Import Modal */}
      <CSVImport
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        showToast={showToast}
        existingItems={snap.assets as Asset[]}
      />

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingItem ? 'Edit Asset' : 'Add New Asset'}
        subtitle={editingItem?.referenceNumber}
        size="xl"
        gradient="from-purple-600 to-indigo-600"
      >
        <AssetForm
          formData={formData}
          setFormData={setFormData}
          onSave={handleSave}
          onCancel={() => setShowModal(false)}
          isEditing={!!editingItem}
        />
      </Modal>

      {/* Audit Modal */}
      <Modal
        isOpen={showAuditModal}
        onClose={() => setShowAuditModal(false)}
        title="Physical Audit"
        subtitle={auditingItem ? getAssetDisplayName(auditingItem) : undefined}
        size="md"
        gradient="from-green-600 to-teal-600"
      >
        {auditingItem && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Expected Quantity</p>
              <p className="text-2xl font-bold">{auditingItem.quantity}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Actual Quantity
              </label>
              <input
                type="number"
                value={auditForm.actualQuantity}
                onChange={(e) => setAuditForm({ ...auditForm, actualQuantity: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Auditor Name
              </label>
              <input
                type="text"
                value={auditForm.auditor}
                onChange={(e) => setAuditForm({ ...auditForm, auditor: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Your name"
              />
            </div>

            {parseInt(auditForm.actualQuantity) !== auditingItem.quantity && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discrepancy Reason
                </label>
                <textarea
                  value={auditForm.discrepancy}
                  onChange={(e) => setAuditForm({ ...auditForm, discrepancy: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Explain the discrepancy..."
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                value={auditForm.notes}
                onChange={(e) => setAuditForm({ ...auditForm, notes: e.target.value })}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Additional notes..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={handleAuditComplete} className="flex-1">
                Complete Audit
              </Button>
              <Button variant="secondary" onClick={() => setShowAuditModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* History Modal */}
      <Modal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        title="Audit History"
        subtitle={historyItem ? getAssetDisplayName(historyItem) : undefined}
        size="lg"
        gradient="from-amber-600 to-orange-600"
      >
        {historyItem && (
          <div className="space-y-4">
            {(!historyItem.auditHistory || historyItem.auditHistory.length === 0) ? (
              <p className="text-center text-gray-500 py-8">No audit history available</p>
            ) : (
              <div className="space-y-3">
                {historyItem.auditHistory.map((audit, idx) => (
                  <div
                    key={idx}
                    className={`p-4 rounded-lg border ${
                      audit.status === 'match'
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-medium">
                        {formatDate(audit.date)}
                      </span>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          audit.status === 'match'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {audit.status === 'match' ? 'Match' : 'Discrepancy'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Auditor: {audit.auditor}
                    </p>
                    <p className="text-sm text-gray-600">
                      Expected: {audit.expectedQuantity} | Actual: {audit.actualQuantity}
                    </p>
                    {audit.discrepancy && (
                      <p className="text-sm text-red-600 mt-1">
                        Reason: {audit.discrepancy}
                      </p>
                    )}
                    {audit.notes && (
                      <p className="text-sm text-gray-500 mt-1">
                        Notes: {audit.notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>

      <ToastContainer />
      </div>
    </div>
  );
}

// Asset Completeness Check
function getAssetMissingFields(item: Asset): string[] {
  const missing: string[] = [];

  if (!item.quantity || item.quantity <= 0) missing.push('Quantity');
  if (!item.location) missing.push('Location');
  if (!item.owner && !item.assetOwner) missing.push('Asset Owner');

  if (item.isISO27001) {
    if (!item.iso27001Category) missing.push('ISO 27001 Category');
    const hasCompleteDatePair =
      (item.auditDate && item.auditPeriodicity) ||
      (item.calibrationDate && item.calibrationPeriodicity) ||
      (item.certificationDate && item.certificationPeriodicity) ||
      (item.testDate && item.testPeriodicity);
    if (!hasCompleteDatePair) missing.push('Compliance Date & Periodicity');
  } else {
    // For non-ISO: check datesApplicable (default true for older assets without this field)
    const datesApplicable = item.datesApplicable !== undefined ? item.datesApplicable : true;
    if (datesApplicable) {
      const hasCompleteDatePair =
        (item.auditDate && item.auditPeriodicity) ||
        (item.calibrationDate && item.calibrationPeriodicity) ||
        (item.certificationDate && item.certificationPeriodicity) ||
        (item.testDate && item.testPeriodicity);
      if (!hasCompleteDatePair) missing.push('Compliance Date & Periodicity');
    }
  }

  return missing;
}

// Asset Row Component
function AssetRow({
  item,
  onEdit,
  onDelete,
  onAudit,
  onQR,
  onHistory,
}: {
  item: Asset;
  onEdit: (item: Asset) => void;
  onDelete: (id: string) => void;
  onAudit: (item: Asset) => void;
  onQR: (item: Asset) => void;
  onHistory: (item: Asset) => void;
}) {
  const expiry = useExpiry(item);
  const missingFields = getAssetMissingFields(item);
  const isIncomplete = missingFields.length > 0;

  return (
    <tr className={isIncomplete ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'}>
      <td className="px-4 py-3 text-sm font-mono text-purple-600">
        {item.referenceNumber}
      </td>
      <td className="px-4 py-3">
        <div>
          <div className="flex items-center gap-1.5">
            <p className="font-medium text-gray-900">{item.make || '\u2014'}</p>
            {isIncomplete && (
              <span
                className="text-amber-500 cursor-help"
                title={`Missing: ${missingFields.join(', ')}`}
              >
                <Icon name="Warning" size={16} />
              </span>
            )}
          </div>
          {expiry.isOverdue && (
            <span className="text-xs text-red-600">Overdue</span>
          )}
          {expiry.isUpcoming && !expiry.isOverdue && (
            <span className="text-xs text-amber-600">Upcoming</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">{item.model || '\u2014'}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{item.assetGroup || '\u2014'}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{item.sku}</td>
      <td className="px-4 py-3 text-sm font-medium">{item.quantity}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{item.location}</td>
      <td className="px-4 py-3">
        {item.isISO27001 ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Yes
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            No
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(item)}
            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition"
            title="Edit"
          >
            <Icon name="Edit" size={16} />
          </button>
          <button
            onClick={() => onAudit(item)}
            className="p-1.5 text-green-600 hover:bg-green-50 rounded transition"
            title="Audit"
          >
            <Icon name="ClipboardCheck" size={16} />
          </button>
          <button
            onClick={() => onQR(item)}
            className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition"
            title="Generate QR/Barcode"
          >
            <Icon name="QrCode" size={16} />
          </button>
          <button
            onClick={() => onHistory(item)}
            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition"
            title="Audit History"
          >
            <Icon name="Clock" size={16} />
          </button>
          <button
            onClick={() => onDelete(item.id)}
            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition"
            title="Delete"
          >
            <Icon name="Trash" size={16} />
          </button>
        </div>
      </td>
    </tr>
  );
}

// Asset Form Component
function AssetForm({
  formData,
  setFormData,
  onSave,
  onCancel,
  isEditing,
}: {
  formData: AssetFormData;
  setFormData: (data: AssetFormData) => void;
  onSave: () => void;
  onCancel: () => void;
  isEditing: boolean;
}) {
  // Force dates applicable if Info Sec is selected
  const effectiveDatesApplicable = formData.isISO27001 ? true : formData.datesApplicable;

  return (
    <div className="space-y-6">
      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Make
          </label>
          <input
            type="text"
            value={formData.make}
            onChange={(e) => setFormData({ ...formData, make: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            placeholder="e.g. Dell, HP, Cisco"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Model
          </label>
          <input
            type="text"
            value={formData.model}
            onChange={(e) => setFormData({ ...formData, model: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            placeholder="e.g. Latitude 5540"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            SKU <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.sku}
            onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            placeholder="SKU/ID"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Asset Group
        </label>
        <input
          type="text"
          value={formData.assetGroup}
          onChange={(e) => setFormData({ ...formData, assetGroup: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          placeholder="e.g. Laptops, Servers, Network Equipment"
        />
        <p className="text-xs text-gray-500 mt-1">e.g. Laptops, Servers, Network Equipment</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
          <input
            type="number"
            value={formData.quantity}
            onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            placeholder="Location"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Asset Owner</label>
        <input
          type="text"
          value={formData.assetOwner || formData.owner}
          onChange={(e) => setFormData({ ...formData, assetOwner: e.target.value, owner: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          placeholder="Owner name or department"
        />
      </div>

      {/* ISO 27001 */}
      <div className="border-t pt-4">
        <div className="flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            id="iso27001"
            checked={formData.isISO27001}
            onChange={(e) => setFormData({ ...formData, isISO27001: e.target.checked, datesApplicable: e.target.checked ? true : formData.datesApplicable })}
            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
          />
          <label htmlFor="iso27001" className="text-sm font-medium text-gray-700">
            Information Security Asset
          </label>
        </div>
        {formData.isISO27001 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Information Security Category <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.iso27001Category}
              onChange={(e) => setFormData({ ...formData, iso27001Category: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select Information Security Category</option>
              {Object.keys(iso27001Categories).map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Dates */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Compliance Dates
            {formData.isISO27001 && (
              <span className="text-sm font-normal text-red-500 ml-2">* Required for Information Security Assets</span>
            )}
          </h3>
          <label className={`flex items-center gap-2 ${formData.isISO27001 ? `opacity-50 cursor-not-allowed` : `cursor-pointer`}`}>
            <input
              type="checkbox"
              checked={effectiveDatesApplicable}
              onChange={(e) => setFormData({ ...formData, datesApplicable: e.target.checked })}
              disabled={formData.isISO27001}
              className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500 disabled:opacity-50"
            />
            <span className="text-sm text-gray-600">Compliance dates applicable</span>
          </label>
        </div>
        
        {effectiveDatesApplicable && (
          <div className="space-y-4">
            {/* Audit */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Audit</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Date</label>
                  <input
                    type="date"
                    value={formData.auditDate}
                    onChange={(e) => setFormData({ ...formData, auditDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Periodicity (Months)</label>
                  <select
                    value={formData.auditPeriodicity}
                    onChange={(e) => setFormData({ ...formData, auditPeriodicity: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">N/A</option>
                    <option value="3">3</option>
                    <option value="6">6</option>
                    <option value="12">12</option>
                    <option value="24">24</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Calibration */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Calibration</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Date</label>
                  <input
                    type="date"
                    value={formData.calibrationDate}
                    onChange={(e) => setFormData({ ...formData, calibrationDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Periodicity (Months)</label>
                  <select
                    value={formData.calibrationPeriodicity}
                    onChange={(e) => setFormData({ ...formData, calibrationPeriodicity: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">N/A</option>
                    <option value="3">3</option>
                    <option value="6">6</option>
                    <option value="12">12</option>
                    <option value="24">24</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Certification */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Certification</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Date</label>
                  <input
                    type="date"
                    value={formData.certificationDate}
                    onChange={(e) => setFormData({ ...formData, certificationDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Periodicity (Months)</label>
                  <select
                    value={formData.certificationPeriodicity}
                    onChange={(e) => setFormData({ ...formData, certificationPeriodicity: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">N/A</option>
                    <option value="3">3</option>
                    <option value="6">6</option>
                    <option value="12">12</option>
                    <option value="24">24</option>
                  </select>
                </div>
              </div>
            </div>

            {/* PAT */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-semibold text-gray-700 mb-3">PAT</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Date</label>
                  <input
                    type="date"
                    value={formData.testDate}
                    onChange={(e) => setFormData({ ...formData, testDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Periodicity (Months)</label>
                  <select
                    value={formData.testPeriodicity}
                    onChange={(e) => setFormData({ ...formData, testPeriodicity: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">N/A</option>
                    <option value="3">3</option>
                    <option value="6">6</option>
                    <option value="12">12</option>
                    <option value="24">24</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          placeholder="Additional notes..."
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t">
        <Button onClick={onSave} className="flex-1">
          {isEditing ? 'Update Asset' : 'Add Asset'}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
